import "./style.scss";

import Basket from "assets/img/svg/basket";
import Plus from "assets/img/svg/plus";
import MessageBlock from "components/shared/MessageBlock";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import type { ITermFormData } from "containers/TermFormContainer";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CalculationBaseColumn } from "store/calculationBaseColumnLabelsStore";

import { isMilestoneThresholdAmountBased } from "../milestoneBaseColumnUtils";
import type { GroupWithChannels } from "../types";

interface Milestone {
    percentage: number | string;
    start: number | string;
    quantity: number | string;
}

interface IMilestonesInputs {
    /**
     * Whether channel accepts milestones or nots.
     */
    hasMilestones: boolean;
    /**
     * The channel and its data.
     */
    group: GroupWithChannels;
    /**
     * Contract-scoped column used for milestone thresholds (drives "ventes" vs currency unit).
     */
    milestoneBaseColumn: CalculationBaseColumn;
    /**
     * Triggers saving the changes within the input inside the channel data (handled by parent)
     */
    onChange: (newChannel: any) => void;
    /**
     * (on edit mode) Data already saved within channel for each milestone.
     */
    propMilestones?: {
        percentage: number | string;
        start: number | string;
        quantity: number | string;
    }[];
    /** error paints the whole milestone line (labels and inputs) in danger color. */
    variant?: "normal" | "error";
    useFormData: Pick<ITermFormData, "setError" | "clearErrors">;
}

/**
 * This component handles the rendering and modifications of each milestone line. These lines can be many or just one if there are no milestones.
 */
const GroupMilestonesInput = ({
    hasMilestones,
    group,
    milestoneBaseColumn,
    onChange,
    propMilestones,
    variant = "normal",
    useFormData,
}: IMilestonesInputs) => {
    const [t] = useTranslation();
    const { currencySymbol } = useCompanySettings();
    const { setError, clearErrors } = useFormData;

    const [milestones, setMilestones] = useState<Milestone[]>(
        propMilestones ?? [{ percentage: "", start: 0, quantity: Infinity }],
    );
    const [isChangeValid, setIsChangeValid] = useState(true);

    const percentageInput = useRef<HTMLInputElement>(null);

    const handleAddMilestone = (indexOfLast: number) => {
        let lastMilestone = milestones[indexOfLast];
        lastMilestone = {
            ...lastMilestone,
            quantity: indexOfLast === 0 ? 1000 : +lastMilestone.start + 999,
        };
        const newStart = (lastMilestone.quantity as number) + 1;

        const milestonesChanged = milestones.map((mile, index) => {
            if (index === indexOfLast) {
                return lastMilestone;
            } else {
                return mile;
            }
        });

        const newMilestones = [
            ...milestonesChanged,
            {
                percentage: lastMilestone.percentage,
                start: newStart,
                quantity: Infinity,
            },
        ];
        setMilestones(newMilestones);
        onChange({ id: group?.id, name: group.name, data: newMilestones });
    };

    const handleValueChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        index: number,
        inputType: "percentage" | "quantity",
    ) => {
        // This line handles commas and stops from writing anything else than digits:
        let newValue: string | number = e.target.value.replace(",", ".").replace(/[A-zÀ-ú"'/\-_`!;?^$]/, "");

        // This line stops people from writing a percentage higher than 100:
        newValue = inputType === "percentage" && (+newValue > 100 || newValue === "100.") ? 100 : newValue;

        // This replaces value in the current milestone:
        let currentMilestone = milestones[index];
        currentMilestone = {
            ...currentMilestone,
            [inputType]: newValue !== "" ? newValue : "",
        };

        // This updates milestones array with new milestone and updates the start value of next milestone if changing the end value:
        const newMilestones = milestones.map((mile, idx) => {
            if (idx === index) {
                return currentMilestone;
            } else if (idx === index + 1) {
                return {
                    ...mile,
                    start: inputType === "quantity" ? (newValue === "" ? 0 : +newValue + 1) : mile.start,
                };
            } else {
                return mile;
            }
        });

        // This checks whether value changed makes sense with other milestones values:
        const isNotValid = newMilestones.some((mile) => {
            if (mile.quantity !== Infinity) {
                return +mile?.quantity < +mile.start;
            }
        });

        if (isNotValid) {
            setError && setError("channels", { message: `Milestone error on group ${group?.id}` });
        } else {
            clearErrors && clearErrors("channels");
        }

        setIsChangeValid(!isNotValid);
        setMilestones(newMilestones);
        onChange({ id: group?.id, name: group.name, data: newMilestones });
    };

    /**
     * Handles deletion of milestone. Deletes only last milestone.
     */
    const handleDeleteMilestone = () => {
        const withLastDeleted = milestones.filter((item) => item.quantity !== Infinity);
        const newMilestones = withLastDeleted.map((mile, index) => {
            if (index === withLastDeleted.length - 1) {
                return { ...mile, quantity: Infinity };
            } else {
                return mile;
            }
        });

        setMilestones(newMilestones);
        onChange({ id: group?.id, name: group.name, data: newMilestones });
    };

    return (
        <>
            <div
                className={`milestones-inputs${variant === "error" ? " milestones-inputs--error" : ""}${
                    !isChangeValid ? " warning" : ""
                }`}
                id={`milestone-inputs-${group?.id}`}
            >
                {milestones.map((mile, index) => {
                    const previousIterationMilestone = index > 0 ? +milestones[index - 1]?.quantity + 1 : 0;

                    const isLastLine = mile.quantity === Infinity || mile.quantity === "Infinity";
                    const mileQuantity = mile.quantity !== Infinity && mile?.quantity;
                    const isAmountBasedThreshold = isMilestoneThresholdAmountBased(milestoneBaseColumn);
                    return (
                        <div
                            className="milestone-line"
                            key={`milestone-${index}`}
                            style={{
                                paddingRight: `${!isLastLine ? "1.45rem" : "0px"}`,
                                marginBottom: `${
                                    !isLastLine || hasMilestones
                                        ? "1rem"
                                        : index === 0 && !hasMilestones
                                          ? "0.5rem"
                                          : "O.5rem"
                                }`,
                                marginTop: `${index === 0 && "0.5rem"}`,
                            }}
                        >
                            <div className="left-side">
                                <span>{previousIterationMilestone ?? 0}</span>
                                <span className="text-grey">
                                    {isLastLine &&
                                        (isAmountBasedThreshold ? currencySymbol : t("MilestonesForm.Sales"))}{" "}
                                    {t("MilestonesForm.To")}
                                </span>
                                {isLastLine ? (
                                    <span>{t("MilestonesForm.InfinityText")}</span>
                                ) : (
                                    <StyledTextField
                                        className={`quantity ${isChangeValid ? "" : "red"}`}
                                        size="small"
                                        inputType="number"
                                        value={!hasMilestones ? "l'infini" : mileQuantity ?? 0}
                                        onChange={(e: any) => handleValueChange(e, index, "quantity")}
                                        placeholder="1000"
                                        onWheel={(e) => (e.target as HTMLElement).blur()}
                                    />
                                )}
                                {!isLastLine && (
                                    <span className="text-grey">
                                        {isAmountBasedThreshold ? currencySymbol : t("MilestonesForm.Sales")}
                                    </span>
                                )}
                            </div>
                            <div className="right-side">
                                <StyledTextField
                                    className="percentage"
                                    size="small"
                                    value={mile.percentage ?? 0}
                                    placeholder="0"
                                    onChange={(e: any) => handleValueChange(e, index, "percentage")}
                                    trueInputRef={percentageInput}
                                    inputType="number"
                                    onWheel={(e) => (e.target as HTMLElement).blur()}
                                />
                                <span className="text-grey">{t("MilestonesForm.Percentage")}</span>
                                {isLastLine && index !== 0 && <Basket onClick={() => handleDeleteMilestone()} />}
                            </div>
                        </div>
                    );
                })}
                {hasMilestones && (
                    <button
                        type="button"
                        className="new-milestone-btn"
                        onClick={() => handleAddMilestone(milestones.length - 1)}
                    >
                        <Plus /> {t("MilestonesForm.NewMilestone")}
                    </button>
                )}
                {!isChangeValid && <MessageBlock text={t("MilestonesForm.ErrorSteps")} type="error" />}
                {percentageInput?.current?.value === "100" && (
                    <MessageBlock text={t("ConnectTitle.PercentageWarning")} type="info" />
                )}
            </div>
        </>
    );
};

export default GroupMilestonesInput;
